/**
 * Nike AI Controller
 * Manages state and communication for the Nike AI Search remote component
 * Exposed globally at window.webShellClient.__WS_INTERNAL__.nikeAI
 */

const STORAGE_KEY = 'nike-ai-search-open-state';
const EVENT_TOGGLE = 'nike-ai-search:toggle';
const EVENT_OPEN = 'nike-ai-search:open';
const EVENT_CLOSE = 'nike-ai-search:close';
const EVENT_SEND_MESSAGE = 'nike-ai-search:send-message';

class NikeAIController {
  constructor() {
    if (NikeAIController.instance) {
      return NikeAIController.instance;
    }
    NikeAIController.instance = this;
    this.isOpen = false;
    this.initialized = false;
    this.triggerElement = null;
  }

  /**
   * Initialize the controller
   * Sets up event listeners (no persistence - Nike AI always starts closed)
   */
  init() {
    if (this.initialized) return;

    if (typeof window === 'undefined') return;

    // Add ESC key handler to close UI
    this.handleEscKey = event => {
      if (event.key === 'Escape' && this.isOpen) {
        console.log('[Nike AI Controller] ESC pressed, closing...');
        this.close('esc');
      }
    };
    window.addEventListener('keydown', this.handleEscKey);

    this.initialized = true;
  }

  /**
   * Cleanup event listeners
   */
  destroy() {
    if (typeof window === 'undefined') return;
    if (this.handleEscKey) {
      window.removeEventListener('keydown', this.handleEscKey);
    }
  }

  /**
   * Get saved open state from localStorage
   * @returns {boolean}
   */
  getSavedState() {
    if (typeof window === 'undefined' || !window.localStorage) return false;

    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return saved === 'true';
    } catch (e) {
      console.warn('[NikeAI] Failed to read from localStorage:', e);
      return false;
    }
  }

  /**
   * Save open state to localStorage
   * @param {boolean} isOpen
   */
  saveState(isOpen) {
    if (typeof window === 'undefined' || !window.localStorage) return;

    try {
      window.localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch (e) {
      console.warn('[NikeAI] Failed to write to localStorage:', e);
    }
  }

  /**
   * Open the Nike AI interface
   * Captures the currently focused element so focus can be restored on close.
   */
  open() {
    console.log('[Nike AI Controller] Opening...');
    this.captureTriggerElement();
    this.isOpen = true;
    this.saveState(true); // Sync localStorage to prevent stale state
    this.dispatchEvent(EVENT_OPEN);
  }

  /**
   * Close the Nike AI interface
   * Includes the original trigger element in the close event so focus can be restored.
   * @param {string} method - How the close was triggered (button, esc, controller)
   */
  close(method = 'controller') {
    console.log('[Nike AI Controller] Closing...', { method });
    const { triggerElement } = this;
    this.isOpen = false;
    this.saveState(false); // Sync localStorage to prevent stale state
    this.dispatchEvent(EVENT_CLOSE, { method, triggerElement });
    this.triggerElement = null;
  }

  /**
   * Toggle the Nike AI interface using in-memory state.
   * Captures trigger element when toggling open; clears it when toggling closed.
   */
  toggle() {
    console.log('[Nike AI Controller] Toggling... current state:', this.isOpen);
    if (!this.isOpen) {
      this.captureTriggerElement();
    }
    this.isOpen = !this.isOpen;
    this.saveState(this.isOpen);
    this.dispatchEvent(EVENT_TOGGLE);
    if (!this.isOpen) {
      this.triggerElement = null;
    }
    console.log('[Nike AI Controller] New state:', this.isOpen);
  }

  /**
   * Capture the currently focused element as the trigger that opened the panel.
   * Used to restore focus when the panel closes (WCAG 2.4.3 Focus Order).
   */
  captureTriggerElement() {
    if (typeof document !== 'undefined' && document.activeElement) {
      this.triggerElement = document.activeElement;
    }
  }

  /**
   * Send a message/query to Nike AI
   * Just dispatches the message event - caller should handle opening Nike AI
   * @param {string} query - The search query to send
   */
  sendMessage(query) {
    console.log('[Nike AI Controller] Sending message:', query);

    // Dispatch message event with query
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(EVENT_SEND_MESSAGE, {
          detail: { query },
        }),
      );
    }
  }

  /**
   * Dispatch a window event for the remote component to listen to
   * @param {string} eventName
   * @param {Object} additionalDetail - Additional detail to include in event (e.g., method for close)
   */
  dispatchEvent(eventName, additionalDetail = {}) {
    if (typeof window === 'undefined') return;

    const detail = { isOpen: this.isOpen, ...additionalDetail };

    console.log('[Nike AI Controller] Dispatching event:', eventName, 'with detail:', detail);
    window.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
      }),
    );
  }

  /**
   * Get current open state
   * @returns {boolean}
   */
  getState() {
    return this.isOpen;
  }
}

// Create singleton instance
const nikeAIController = new NikeAIController();

/**
 * Initialize Nike AI on window.webShellClient.__WS_INTERNAL__
 */
export function initializeNikeAI() {
  if (typeof window === 'undefined') {
    console.log('[Nike AI] Window is undefined, skipping initialization');
    return;
  }

  console.log('[Nike AI] Initializing controller...');

  // Wait for webShellClient to be available
  const initController = () => {
    if (!window.webShellClient) {
      console.log('[Nike AI] webShellClient not yet available, retrying...');
      setTimeout(initController, 100);
      return;
    }

    // Get the existing __WS_INTERNAL__ object (read-only getter)
    const wsInternal = window.webShellClient.__WS_INTERNAL__;

    if (!wsInternal) {
      console.error('[Nike AI] __WS_INTERNAL__ not found on webShellClient');
      return;
    }

    // Add Nike AI controller to the existing __WS_INTERNAL__ object
    wsInternal.nikeAI = {
      open: () => nikeAIController.open(),
      close: method => nikeAIController.close(method),
      toggle: () => nikeAIController.toggle(),
      sendMessage: query => nikeAIController.sendMessage(query),
      getState: () => nikeAIController.getState(),
    };

    // Initialize controller
    nikeAIController.init();

    console.log(
      '[Nike AI] Controller initialized and exposed at window.webShellClient.__WS_INTERNAL__.nikeAI',
    );
  };

  initController();
}

export default nikeAIController;
